/**
 * Indian financial year resolution (spec §5).
 *
 * "FY computed from the document date: month >= April → FY starts that year;
 *  else the previous year. Timezone Asia/Kolkata."
 *
 * The timezone is load-bearing: a document saved at 00:30 IST on 1 April is
 * still 19:00 UTC on 31 March, and the machine running the API may be in any
 * zone at all. Calendar parts are therefore always read through
 * `Intl.DateTimeFormat` pinned to Asia/Kolkata — never `Date#getMonth()`.
 */
import { assertInteger } from "./rounding.js";
/** The Indian financial year starts on 1 April. */
export const FY_START_MONTH = 4;
export const INDIA_TIME_ZONE = "Asia/Kolkata";
const IST_FORMATTER = new Intl.DateTimeFormat("en-US", {
    timeZone: INDIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});
/** Reads a Date's calendar day as it is in India, regardless of machine zone. */
export function istCalendarParts(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        throw new Error(`A valid Date is required, received ${String(date)}`);
    }
    let year;
    let month;
    let day;
    for (const part of IST_FORMATTER.formatToParts(date)) {
        if (part.type === "year") {
            year = Number(part.value);
        }
        else if (part.type === "month") {
            month = Number(part.value);
        }
        else if (part.type === "day") {
            day = Number(part.value);
        }
    }
    if (year === undefined || month === undefined || day === undefined) {
        throw new Error(`Could not read ${INDIA_TIME_ZONE} calendar parts from ${date.toISOString()}`);
    }
    return { year, month, day };
}
function twoDigitYear(year) {
    return String(((year % 100) + 100) % 100).padStart(2, "0");
}
/** Builds a {@link FinancialYear} from its start year, e.g. 2026 → 2026-27. */
export function financialYearFromStartYear(startYear) {
    assertInteger(startYear, "financialYearFromStartYear startYear");
    const endYear = startYear + 1;
    const startYY = twoDigitYear(startYear);
    const endYY = twoDigitYear(endYear);
    return {
        startYear,
        endYear,
        fyShort: `${startYY}-${endYY}`,
        quoteYY: startYY,
        label: `${startYear}-${endYY}`,
    };
}
/**
 * The Indian financial year containing `date`, evaluated in Asia/Kolkata.
 *
 * @example financialYearFor(new Date("2026-04-01T00:00:00+05:30")).fyShort === "26-27"
 * @example financialYearFor(new Date("2026-03-31T23:59:00+05:30")).fyShort === "25-26"
 */
export function financialYearFor(date) {
    const { year, month } = istCalendarParts(date);
    const startYear = month >= FY_START_MONTH ? year : year - 1;
    return financialYearFromStartYear(startYear);
}
//# sourceMappingURL=financial-year.js.map